"use client"

import { BlockchainFiltersMoleculeReatom } from "@/app/blockchains/filters/blockchain-filters-molecule.reatom"
import type { BlockchainFilter } from "@/app/blockchains/filters/blockchain-filters-scope"
import { Button } from "@rarible_int/anaconda_ui_components"
import { reatomComponent } from "@reatom/react"
import { useMolecule } from "bunshi/react"
import { AnimatePresence, motion } from "motion/react"
import { useEffect } from "react"
import { useState } from "react"
import { createContext, useContext } from "react"
import { CloseIcon } from "../../icons/close"
import { useBannerContent } from "./use-banner"

const getBannerStorageKey = (blockchain: BlockchainFilter) =>
  `banner-last-shown-${blockchain}`

interface BannerContextType {
  isBannerClosed: boolean
  closeBanner: () => void
  banner:
    | { message: string; timestamp: string; blockchain: BlockchainFilter }
    | undefined
}

const BannerContext = createContext<BannerContextType>({
  isBannerClosed: false,
  closeBanner: () => {},
  banner: undefined,
})

export const BannerProviderReatom = reatomComponent<{
  children: React.ReactNode
}>(({ children }) => {
  const [isBannerClosed, setIsBannerClosed] = useState(false)
  const { selectedBlockchain } = useMolecule(BlockchainFiltersMoleculeReatom)

  // Call atom directly - requires reatomComponent wrapper
  const blockchain = selectedBlockchain()

  const data = useBannerContent()

  const banner =
    data?.find((banner) => banner.blockchain === blockchain) ??
    data?.find((banner) => banner.blockchain === "ALL")

  useEffect(() => {
    if (!banner?.message?.trim()) {
      setIsBannerClosed(true)
      return
    }

    const storageKey = getBannerStorageKey(banner.blockchain)
    const lastShownTimestamp = localStorage.getItem(storageKey)

    if (!lastShownTimestamp || lastShownTimestamp !== banner.timestamp) {
      setIsBannerClosed(false)
    } else {
      setIsBannerClosed(true)
    }

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === storageKey) {
        const newLastShownTimestamp = localStorage.getItem(storageKey)
        setIsBannerClosed(
          !!newLastShownTimestamp && newLastShownTimestamp === banner.timestamp,
        )
      }
    }

    window.addEventListener("storage", handleStorageChange)
    return () => window.removeEventListener("storage", handleStorageChange)
  }, [banner])

  const closeBanner = () => {
    const storageKey = getBannerStorageKey(banner?.blockchain ?? blockchain)
    localStorage.setItem(storageKey, banner?.timestamp ?? "")
    setIsBannerClosed(true)
  }

  return (
    <BannerContext.Provider value={{ isBannerClosed, closeBanner, banner }}>
      {children}
    </BannerContext.Provider>
  )
}, "BannerProviderReatom")

export function useBannerState() {
  return useContext(BannerContext)
}

export function Banner() {
  const { isBannerClosed, closeBanner, banner } = useBannerState()

  if (!banner?.message?.trim()) {
    return null
  }

  return (
    <AnimatePresence>
      {!isBannerClosed && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="relative border-foreground/20 border-b-[1px] bg-background/100 p-4 pb-[15px] text-center text-foreground/100 text-xs [&_a]:text-brand"
        >
          <div className="relative">
            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: temporary solution */}
            <span dangerouslySetInnerHTML={{ __html: banner.message }} />
            <Button
              variant="ghost"
              className="absolute top-0 right-0 h-4 w-4 p-0 opacity-60 hover:bg-transparent hover:opacity-100"
              onClick={closeBanner}
            >
              <CloseIcon className="h-4 w-4" />
            </Button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
